import React, { useEffect, useState } from 'react';
import ApiService from '../../services/ApiService';
import Loader from '../../components/Loader';
import Paginator from '../../components/Paginator';
import { toast } from 'react-toastify';
import { formatCurrency, formatDate } from '../../utils/helper';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const List = () => {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [pagination, setPagination] = useState({});
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [apiData, setApiData] = useState({
        page: 1,
        search: "",
        start_date: "",
        end_date: "",
        type: ""
    });

    useEffect(() => {
        getData();
    }, [apiData.page, apiData.search, apiData.start_date, apiData.end_date, apiData.type]);

    const handleDateChange = (update) => {
        setDateRange(update);
        const [start, end] = update;
        setApiData(prev => ({
            ...prev,
            start_date: start ? formatDate(start, 'YYYY-MM-DD') : "",
            end_date: end ? formatDate(end, 'YYYY-MM-DD') : ""
        }));
    };

    const getData = async () => {
        setIsLoading(true);
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/transactions',
                data: apiData,
            });
            if (response.data.status) {
                const transactions = response.data.data.transactions;
                setData(transactions || []);
                setPagination(response.data.data.pagination || {});
            }
        } catch (error) {
            toast.error('Failed to fetch transactions');
        } finally {
            setIsLoading(false);
        }
    };

    const handleExport = () => {
        const csvRows = [];
        const headers = ['Date', 'Type', 'Amount', 'Status', 'Transaction ID', 'Customer', 'Payment Method'];
        csvRows.push(headers.join(','));

        data.forEach(item => {
            const values = [
                `"${formatDate(item.created_at)}"`,
                `"${item.type}"`,
                `"${item.amount}"`,
                `"${item.status}"`,
                `"${item.stripe_transaction_id || ''}"`,
                `"${item.appointment?.customer?.name || ''}"`,
                `"${item.payment_method || ''}"`
            ];
            csvRows.push(values.join(','));
        });

        const csvContent = 'data:text/csv;charset=utf-8,' + csvRows.join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `transactions_report_${new Date().getTime()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Transactions</h1>
                    </div>
                </div>
            </div>
            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="card-title">
                                <div className="d-flex align-items-center position-relative my-1">
                                    <span className="svg-icon svg-icon-1 position-absolute ms-6">
                                        <i className="fas fa-search fs-1"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control form-control-solid w-250px ps-14"
                                        placeholder="Search Transaction ID / Type / Customer"
                                        value={apiData.search}
                                        onChange={(e) => setApiData({ ...apiData, search: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="card-toolbar">
                                <div className="d-flex justify-content-end gap-3 align-items-center">
                                    <div className="w-200px">
                                        <select 
                                            className="form-select form-select-solid"
                                            value={apiData.type}
                                            onChange={(e) => setApiData({...apiData, type: e.target.value})}
                                        >
                                            <option value="">All Types</option>
                                            <option value="deposit">Deposit</option>
                                            <option value="payment">Payment</option>
                                            <option value="cancel_fee">Cancellation Fee</option>
                                            <option value="no_show_fee">No Show Fee</option>
                                            <option value="refund">Refund</option>
                                        </select>
                                    </div>
                                    <div className="w-200px">
                                         <DatePicker
                                            selectsRange={true}
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={handleDateChange}
                                            className="form-control form-control-solid"
                                            placeholderText="Select Date Range"
                                            isClearable={true}
                                        />
                                    </div>
                                    <button 
                                        type="button" 
                                        className="btn btn-light-primary btn-sm"
                                        onClick={handleExport}
                                        disabled={data.length === 0}
                                    >
                                        <i className="fas fa-download me-2"></i> Export CSV
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="card-body py-4">
                            {isLoading ? <Loader /> : (
                                <>
                                    <div className="table-responsive">
                                        <table className="table align-middle table-row-dashed fs-6 gy-5">
                                            <thead>
                                                <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                                    <th>Date</th>
                                                    <th>Appointment #</th>
                                                    <th>Type</th>
                                                    <th>Customer</th>
                                                    <th>Amount</th>
                                                    <th>Status</th>
                                                    <th>Transaction ID</th>
                                                    <th>Payment Method</th>
                                                </tr>
                                            </thead>
                                            <tbody className="text-gray-600 fw-semibold">
                                                {data.map((item) => (
                                                    <tr key={item.id}>
                                                        <td>{formatDate(item.created_at, 'MMM D, YYYY HH:mm')}</td>
                                                        <td>{item.appointment?.ref_no || '-'}</td>
                                                        <td className="text-capitalize">{item.type.replace('_', ' ')}</td>
                                                        <td>
                                                            {item.appointment?.customer ? (
                                                                <div className="d-flex flex-column">
                                                                    <span className="text-gray-800 mb-1">{item.appointment.customer.name}</span>
                                                                    <span className="text-muted fs-7">{item.appointment.customer.email}</span>
                                                                </div>
                                                            ) : '-'}
                                                        </td>
                                                        <td>{formatCurrency(item.amount)}</td>
                                                        <td>
                                                            <span className={`badge badge-light-${item.status === 'success' ? 'success' : 'danger'}`}>
                                                                {item.status}
                                                            </span>
                                                        </td>
                                                        <td>{item.stripe_transaction_id || '-'}</td>
                                                        <td className="text-uppercase">{item.payment_method || '-'}</td>
                                                    </tr>
                                                ))}
                                                {data.length === 0 && (
                                                    <tr>
                                                        <td colSpan="7" className="text-center">No transactions found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    <Paginator
                                        pagination={pagination}
                                        onPageChange={(page) => setApiData((prev) => ({ ...prev, page }))}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default List;